import type { PackForSaleRow } from "@/actions/packs";
import { TabsContent } from "@/components/ui/tabs";
import type { PackStripContext } from "@/features/packs/components/pack-strip";
import type { ViewerPackCoverage } from "@/features/packs/lib/eligibility";
import { StoryHighlightsRow } from "@/features/stories/components/highlights/story-highlights-row.client";
import type { SocialLinks } from "@/lib/contact-links";
import type { MediaAsset } from "@/lib/media/types";
import { EventDetailsContent } from "./event-details-content";
import type { NormalizedOccurrence } from "./event-page.shared";

interface HiiveInfo {
  name: string;
  logoImage: MediaAsset | null;
}

interface EventDetailsTabProps {
  event: {
    id: number;
    description: string;
    pattern: string;
    venueName: string;
    address: string;
    latitude: number | null;
    longitude: number | null;
    discoveryScope: "venue" | "areas" | "everywhere";
    visibilityAreas: Array<{ id: number; label: string }>;
    highlightStandsOut: string | null;
    highlightExperience: string | null;
    highlightSpecialAttractions: string | null;
    highlightPerfectFor: string | null;
    allowedGenders?: Array<"male" | "female" | "other">;
    contactPhone?: string | null;
    contactEmail?: string | null;
    websiteUrl?: string | null;
    socialLinks?: SocialLinks | null;
    thingsToKnow: string[];
    amenities: string[];
    termsAndConditions: Array<{ title: string; description?: string | null }>;
    faqs?: Array<{ title: string; description?: string | null }>;
    /** Accepted partner Hiives, shown under "Hosted by". */
    collaborators?: Array<{
      id: number;
      name: string;
      slug: string;
      logoImage: unknown;
    }>;
  };
  scheduleOccurrence?: NormalizedOccurrence | null;
  scheduleOccurrences?: NormalizedOccurrence[];
  distanceKm: number | null;
  hiive?: HiiveInfo;
  hiiveMemberCount?: number;
  hiiveEventCount?: number;
  packs?: PackForSaleRow[];
  packStripContext?: PackStripContext | null;
  viewerPackHolding?: ViewerPackCoverage | null;
  hiiveSlug?: string;
}

export function EventDetailsTab({
  event,
  scheduleOccurrence,
  scheduleOccurrences = [],
  distanceKm,
  hiive,
  hiiveMemberCount,
  hiiveEventCount,
  packs,
  packStripContext,
  viewerPackHolding,
  hiiveSlug,
}: EventDetailsTabProps) {
  return (
    <TabsContent value="details" className="space-y-6 px-3">
      <StoryHighlightsRow ownerType="event" ownerId={String(event.id)} />
      <EventDetailsContent
        event={event}
        scheduleOccurrence={scheduleOccurrence ?? null}
        scheduleOccurrences={scheduleOccurrences}
        distanceKm={distanceKm}
        hiive={hiive}
        hiiveMemberCount={hiiveMemberCount}
        hiiveEventCount={hiiveEventCount}
        packs={packs}
        packStripContext={packStripContext}
        viewerPackHolding={viewerPackHolding}
        hiiveSlug={hiiveSlug}
      />
    </TabsContent>
  );
}
